/**
 * BulkInviteDialog — Platform Admin > Davetler > "Toplu Davet".
 *
 * E-posta listesi yapıştırılır, deneme süresi + pazarlama şablonu seçilir,
 * istenirse düzenlenir, önce test atılır, sonra herkese kişiye özel davet
 * linkiyle gönderilir.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Megaphone, Send, Users as UsersIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const TRIAL_OPTIONS = [1, 3, 6, 12];

function parseEmails(text) {
  const tokens = (text || "").split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
  const valid = [];
  const invalid = [];
  const seen = new Set();
  for (const t of tokens) {
    const e = t.toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      if (!seen.has(e)) {
        valid.push(e);
        seen.add(e);
      }
    } else {
      invalid.push(t);
    }
  }
  return { valid, invalid };
}

export default function BulkInviteDialog({ open, onOpenChange, onSent }) {
  const { user } = useAuthStore();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [modules, setModules] = useState([]);
  const [emailsText, setEmailsText] = useState("");
  const [trialMonths, setTrialMonths] = useState(3);
  const [campaign, setCampaign] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { valid: validEmails, invalid: invalidEmails } = useMemo(
    () => parseEmails(emailsText),
    [emailsText],
  );

  useEffect(() => {
    if (!open) return;
    setTestTo((prev) => prev || user?.email || "");
    api
      .get("/platform/tenant-invitations/templates")
      .then(({ data }) => {
        setTemplates(data.templates || []);
        setModuleCatalog(data.modules || []);
        if (!templateId && data.templates?.length) {
          applyTemplate(data.templates[0]);
        }
        if (data.default_trial_months) {
          setTrialMonths((prev) => prev || data.default_trial_months);
        }
      })
      .catch(() => toast.error("Şablonlar yüklenemedi"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyTemplate = (tpl) => {
    setTemplateId(tpl.id);
    setSubject(tpl.subject);
    setBody(tpl.body);
    setCtaLabel(tpl.cta_label);
  };

  const payload = () => ({
    trial_months: trialMonths,
    campaign: campaign.trim(),
    subject: subject.trim(),
    body,
    cta_label: ctaLabel.trim(),
    modules,
  });

  const toggleModule = (id) =>
    setModules((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const valid = subject.trim() && body.trim();

  const sendTest = async () => {
    if (!valid || !testTo.trim()) return;
    setSendingTest(true);
    try {
      const { data } = await api.post("/platform/tenant-invitations/bulk", {
        ...payload(),
        test_to: testTo.trim(),
      });
      if (data?.delivery?.delivered) {
        toast.success(`Test daveti ${testTo.trim()} adresine gönderildi`);
      } else {
        toast.warning("Gönderim outbox'a düştü — SendGrid yapılandırmasını kontrol edin");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test gönderilemedi");
    } finally {
      setSendingTest(false);
    }
  };

  const sendAll = async () => {
    setConfirmOpen(false);
    setSendingAll(true);
    try {
      const { data } = await api.post("/platform/tenant-invitations/bulk", {
        ...payload(),
        emails: validEmails,
      });
      const skippedNote = data.skipped?.length ? `, ${data.skipped.length} atlandı (zaten kayıtlı/davetli)` : "";
      toast.success(`Davetler gönderildi — ${data.sent}/${data.total} başarılı${skippedNote}`);
      onSent?.(data);
      onOpenChange(false);
      setEmailsText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Davetler gönderilemedi");
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="bulk-invite-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-violet-600" /> Toplu Davet — Yeni Üye Kampanyası
            </DialogTitle>
            <DialogDescription>
              Listedeki her adrese kişiye özel davet linki üretilir; alıcı tek
              tıkla şirket hesabını oluşturur ve deneme süresi otomatik tanımlanır.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="bulk-emails">
                E-posta listesi{" "}
                <span className="font-normal text-zinc-400">(virgül, boşluk veya satır ile ayırın)</span>
              </Label>
              <Textarea
                id="bulk-emails"
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                rows={4}
                placeholder={"ahmet@firma.com, ayse@sirket.com\nmehmet@ornek.com"}
                data-testid="bulk-invite-emails-input"
                className="mt-1 font-mono text-xs"
              />
              <div className="mt-1 flex flex-wrap gap-3 text-[11px]">
                <span className="text-emerald-600" data-testid="bulk-invite-valid-count">
                  ✓ {validEmails.length} geçerli adres
                </span>
                {invalidEmails.length > 0 && (
                  <span className="text-red-500" data-testid="bulk-invite-invalid-count">
                    ✗ {invalidEmails.length} geçersiz: {invalidEmails.slice(0, 3).join(", ")}
                    {invalidEmails.length > 3 ? "…" : ""}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ücretsiz deneme süresi</Label>
                <Select
                  value={String(trialMonths)}
                  onValueChange={(v) => setTrialMonths(Number(v))}
                >
                  <SelectTrigger className="mt-1" data-testid="bulk-invite-trial-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIAL_OPTIONS.map((m) => (
                      <SelectItem key={m} value={String(m)} data-testid={`bulk-invite-trial-${m}`}>
                        <span className="inline-flex items-center gap-1.5">
                          <Gift className="h-3.5 w-3.5 text-violet-500" /> {m} ay ücretsiz
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Şablon</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      title={tpl.description}
                      data-testid={`bulk-invite-template-${tpl.id}`}
                      className={[
                        "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                        templateId === tpl.id
                          ? "bg-violet-600 text-white"
                          : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
                      ].join(" ")}
                    >
                      {tpl.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>
                Kampanya Adı{" "}
                <span className="font-normal text-zinc-400">
                  (dönüşüm karşılaştırması için — örn. "Haziran LinkedIn")
                </span>
              </Label>
              <Input
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                maxLength={60}
                placeholder="Boş bırakılırsa 'Genel' sayılır"
                className="mt-1"
                data-testid="bulk-invite-campaign-input"
              />
            </div>

            {moduleCatalog.length > 0 && (
              <div>
                <Label>
                  Deneme paketine dahil modüller{" "}
                  <span className="font-normal text-zinc-400">(birden fazla seçilebilir)</span>
                </Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {moduleCatalog.map((m) => {
                    const on = modules.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleModule(m.id)}
                        title={m.description}
                        data-testid={`bulk-invite-module-${m.id}`}
                        className={[
                          "rounded-full px-3 py-1.5 text-xs font-medium transition-colors ring-1",
                          on
                            ? "bg-emerald-600 text-white ring-emerald-600"
                            : "bg-white text-zinc-700 ring-zinc-200 hover:bg-zinc-50",
                        ].join(" ")}
                      >
                        {on ? "✓ " : "+ "}
                        {m.name}
                        {m.price_usd > 0 && (
                          <span className={on ? "ml-1 opacity-80" : "ml-1 text-zinc-400"}>
                            (${m.price_usd}/ay değerinde)
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-zinc-400">
                  Seçilen modüller davet edilen şirketin ücretsiz deneme paketine dahil edilir.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="bulk-subject">Konu</Label>
              <Input
                id="bulk-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="bulk-invite-subject-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bulk-body">
                İçerik{" "}
                <span className="font-normal text-zinc-400">
                  (HTML destekli — {"{{trial_months}}"}, {"{{trial_days}}"} otomatik doldurulur)
                </span>
              </Label>
              <Textarea
                id="bulk-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                data-testid="bulk-invite-body-input"
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label htmlFor="bulk-cta">Buton yazısı</Label>
              <Input
                id="bulk-cta"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                data-testid="bulk-invite-cta-input"
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-zinc-400">
                Buton her alıcının kişiye özel davet linkine gider.
              </p>
            </div>

            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
              <Label htmlFor="bulk-test-to" className="text-xs text-zinc-600">
                Önce test gönder (davet oluşturulmaz, yalnızca önizleme)
              </Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  id="bulk-test-to"
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="ornek@firma.com"
                  data-testid="bulk-invite-test-to-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendTest}
                  disabled={!valid || !testTo.trim() || sendingTest}
                  data-testid="bulk-invite-test-btn"
                  className="shrink-0 gap-1.5"
                >
                  {sendingTest ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  Test gönder
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="items-center gap-2 sm:justify-between">
            <span
              className="inline-flex items-center gap-1.5 text-xs text-zinc-500"
              data-testid="bulk-invite-recipient-count"
            >
              <UsersIcon className="h-3.5 w-3.5" /> {validEmails.length} adrese davet gidecek
            </span>
            <Button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!valid || validEmails.length === 0 || sendingAll}
              data-testid="bulk-invite-send-all-btn"
              className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
            >
              {sendingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Megaphone className="h-4 w-4" />
              )}
              {validEmails.length} kişiye davet gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bulk-invite-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Davetler gönderilsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {validEmails.length} adrese, her biri için {trialMonths} ay
              ücretsiz deneme tanımlı kişiye özel davet linki gönderilecek.
              Zaten kayıtlı veya davetli adresler otomatik atlanır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-invite-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={sendAll}
              data-testid="bulk-invite-confirm-btn"
              className="bg-violet-600 hover:bg-violet-700"
            >
              Evet, gönder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
